import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@/contexts/NavigationContext'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { useAuth } from '@/contexts/AuthContext'
import { useSwipeGesture } from '@/hooks/useSwipeGesture'
import { cn } from '@/lib/cn'
import { NavIcon } from './NavIcon'
import { FEATURE_NAV, SECONDARY_NAV } from './nav-items'

/**
 * Mobile slide-out drawer — features + settings + logout.
 */
export function SideDrawer() {
  const { t, i18n } = useTranslation('common')
  const { drawerOpen, closeDrawer, setRoute, route } = useNavigation()
  const { logout, isAuthenticated } = useAuth()
  const [logoutOpen, setLogoutOpen] = useState(false)
  const isRtl = i18n.dir() === 'rtl'

  const swipe = useSwipeGesture({
    onSwipeLeft: () => {
      if (!isRtl) closeDrawer()
    },
    onSwipeRight: () => {
      if (isRtl) closeDrawer()
    },
  })

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen, closeDrawer])

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [drawerOpen])

  const go = (id: typeof route) => {
    setRoute(id)
    closeDrawer()
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-drawer lg:hidden',
          drawerOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!drawerOpen}
      >
        <button
          type="button"
          className={cn(
            'absolute inset-0 bg-black/55 transition-opacity',
            drawerOpen ? 'opacity-100' : 'opacity-0',
          )}
          aria-label={t('drawer.close')}
          onClick={closeDrawer}
          tabIndex={drawerOpen ? 0 : -1}
        />

        <aside
          {...swipe}
          className={cn(
            'absolute inset-y-0 flex w-[min(18rem,86vw)] flex-col',
            'bg-terminal-bg text-terminal-fg shadow-2xl transition-transform duration-300 ease-out',
            'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
            isRtl ? 'right-0 border-s' : 'left-0 border-e',
            'border-terminal-border/40',
            drawerOpen
              ? 'translate-x-0'
              : isRtl
                ? 'translate-x-full'
                : '-translate-x-full',
          )}
          role="dialog"
          aria-modal="true"
          aria-label={t('drawer.open')}
        >
          <div className="flex h-12 items-center justify-between border-b border-terminal-border/40 px-3">
            <BrandLogo height={22} />
            <button
              type="button"
              onClick={closeDrawer}
              className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-terminal-muted hover:bg-terminal-surface"
              aria-label={t('drawer.close')}
            >
              ✕
            </button>
          </div>

          <nav className="terminal-scroll flex flex-1 flex-col gap-0.5 overflow-y-auto p-2">
            {FEATURE_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                className={cn(
                  'flex h-9 items-center gap-2.5 rounded-sm px-2.5 text-xs',
                  route === item.id
                    ? 'bg-terminal-elevated text-accent'
                    : 'text-terminal-muted hover:bg-terminal-surface hover:text-terminal-fg',
                )}
              >
                <NavIcon name={item.icon} className="h-4 w-4" />
                {t(item.labelKey)}
              </button>
            ))}

            <div className="my-1.5 border-t border-terminal-border/40" />

            {SECONDARY_NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                className={cn(
                  'flex h-9 items-center gap-2.5 rounded-sm px-2.5 text-xs',
                  route === item.id
                    ? 'bg-terminal-elevated text-accent'
                    : 'text-terminal-muted hover:bg-terminal-surface hover:text-terminal-fg',
                )}
              >
                <NavIcon name={item.icon} className="h-4 w-4" />
                {t(item.labelKey)}
              </button>
            ))}

            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => setLogoutOpen(true)}
                className="mt-2 flex h-9 items-center gap-2.5 rounded-sm px-2.5 text-xs text-danger hover:bg-danger/10"
              >
                {t('actions.logout')}
              </button>
            ) : null}
          </nav>

          <div className="flex items-center justify-between gap-2 border-t border-terminal-border/40 p-3">
            <LanguageSwitcher />
            <ThemeSwitcher />
          </div>
        </aside>
      </div>

      <ConfirmModal
        open={logoutOpen}
        title={t('logout_confirm_title')}
        message={t('logout_confirm_message')}
        tone="danger"
        confirmLabel={t('actions.logout')}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={() => {
          void logout().finally(() => {
            setLogoutOpen(false)
            closeDrawer()
            window.location.assign('/login')
          })
        }}
      />
    </>
  )
}
